import {
    formatToLocalDate,
    getDefaultEndDate,
    getDefaultStartDate,
} from '@/lib/date-util';
import { getEnumKeyByEnumValue } from '@/lib/helper/event-flow-helper';
import EventFlowStep from '@/model/enums/event-flow/event-flow-step';
import EventType from '@/model/enums/EventType';
import { User } from '@/model/Profile';
import { Project } from '@/model/Project';

export type EventResponse = {
    id: number;
    projectId?: number;
    name: string;
    description: string;
    startDate: Date;
    type: EventType;
    projectName: string;
    role: string;
    selectedStartDate: Date;
    selectedEndDate: Date;
    step: EventFlowStep | keyof typeof EventFlowStep | null;
    createdAt: Date | null;
    deletedAt: Date | null;
    createdBy: User;
};

export type InviteUserRequest = {
    eventId: number;
    userId: number;
};

export type RemoveUserRequest = InviteUserRequest;

export type EventRequest = {
    name: string;
    description: string;
    startDate: string | null;
    type: EventType | null;
    selectedStartDate: Date;
    selectedEndDate: Date;
    step: EventFlowStep | keyof typeof EventFlowStep | null;
};

export type EventFlowSettings = {
    selectedStartDate: Date;
    selectedEndDate: Date;
    step: EventFlowStep | keyof typeof EventFlowStep | null;
};

export type EventFlowSettingsResponse = EventFlowSettings & {
    startDate: Date;
};

export type EventFormData = {
    projectId: number | null;
    startDate: Date | null;
    name: string;
    description: string;
    type: EventType | null;
};

export type EventUpdateData = EventFormData & EventFlowSettings;

export type EventUpdate = {
    eventId: number | null;
    eventRequest: EventUpdateData;
};

export type EventFlowSettingsUpdate = {
    eventId: number;
    selectedDate: EventFlowSettings;
};

export type DuplicateEventRequest = EventRequest & {
    duplicatedEventId: number;
};

export type DuplicateEventSource = {
    duplicatedEvent: EventResponse;
    sourceStartDate: Date;
};

export function resolveEventProjectId(
    event: EventResponse | null,
    projects: Project[],
): number | null {
    if (!event) {
        return null;
    }

    if (event.projectId != null) {
        return event.projectId;
    }

    return (
        projects.find((project) => project.name === event.projectName)?.id ??
        null
    );
}

function serializeEventStep(
    step: EventFlowStep | keyof typeof EventFlowStep | null,
): keyof typeof EventFlowStep | null {
    if (!step) {
        return null;
    }

    if (typeof step === 'string') {
        return step as keyof typeof EventFlowStep;
    }

    return EventFlowStep[step] as keyof typeof EventFlowStep;
}

export function createEventRequestFromFormData(
    formData: EventFormData,
): EventRequest {
    const { name, startDate, description, type } = formData;

    return {
        name,
        startDate: formatToLocalDate(startDate),
        description,
        type,
        selectedStartDate: getDefaultStartDate(startDate ?? new Date()),
        selectedEndDate: getDefaultEndDate(startDate ?? new Date()),
        step: getEnumKeyByEnumValue(
            EventFlowStep,
            EventFlowStep.THIRTY_MINUTES,
        ),
    };
}

export function createDuplicateEventRequestFromFormData(
    formData: EventFormData,
    duplicateEventSource: DuplicateEventSource,
): DuplicateEventRequest {
    const startDate =
        formData.startDate ?? duplicateEventSource.sourceStartDate;
    const offset =
        startDate.getTime() - duplicateEventSource.sourceStartDate.getTime();

    return {
        name: formData.name,
        description: formData.description,
        startDate: formatToLocalDate(formData.startDate),
        selectedStartDate: new Date(
            duplicateEventSource.duplicatedEvent.selectedStartDate.getTime() +
                offset,
        ),
        selectedEndDate: new Date(
            duplicateEventSource.duplicatedEvent.selectedEndDate.getTime() +
                offset,
        ),
        step: serializeEventStep(duplicateEventSource.duplicatedEvent.step),
        type: formData.type,
        duplicatedEventId: duplicateEventSource.duplicatedEvent.id,
    };
}

export function createEventUpdateRequest(
    eventData: EventUpdateData,
): EventRequest {
    return {
        ...eventData,
        startDate:
            eventData.startDate instanceof Date
                ? formatToLocalDate(eventData.startDate)
                : eventData.startDate ?? null,
    };
}
